<script>
    import { link } from "svelte-spa-router";
    import SlicePicker from "../components/SlicePicker.svelte";
    import WideCentershiftPreview from "../components/WideCentershiftPreview.svelte";
    import BeamhardCuppingPreview from "../components/BeamhardCuppingPreview.svelte";
    import PhaseRetrievalPreview from "../components/PhaseRetrievalPreview.svelte";
    import FinalPreview from "../components/FinalPreview.svelte";
    import Preview from "../components/Preview.svelte";
    import { pipelineObserver } from '../pipelinestores.js';
    import {
        selectedPreview,
        roi,
        maximumVolume,
        finalFdkSettings,
        histogramThreshold,
        lastPreviewImageIndex,
        lastBeamhardCuppingPreviewImageIndex,
        lastCentershiftPreviewImageIndex,
        centershiftGapStack,
        zoom,
        offset
    } from "../previewstores.js";
    import {
        getNumberOfAvailablePreviewsForSlice,
        extractPhaseRetrievalBaseFromPath,
        extractLatestPhaseRetrievalBaseAndTime,
        extractPreprocessingBaseFromPath,
        timeComparator,
        phaseRetrievalComparator,
        now,
        getTomographyDetailsApi,
        getGroupedPreviewDetailsApi,
        extractDatasetNameFromPath
    } from "../utils.js";
    import { hostname, isAuthenticated, showDebug, stack, currentSession, currentTomography } from "../stores.js";
    import Header from "../components/Header.svelte";
    import { toast } from "@zerodevx/svelte-toast";
    import { toastThemeError, toastThemeSuccess } from "../toasts.js";
    import DataTree from "../components/DataTree.svelte";
    import { onMount, onDestroy } from "svelte";
    import Refresh from "svelte-material-icons/Refresh.svelte";
    import { tooltip } from "svooltip";

    export let params = {}; // svelte-spa-router needs this for route parameters
    let pageName = "Recipe Page";
    let value = {
        session: params.session,
        tomography: params.tomography,
    };
    let tomography;

    // DEBUG: For debugging, add a console printout to see when initialisation has been run.
    // Please note that since initialiseData is async, it doesn't mean that the function has 
    // been completed, i.e. initialiseDone may be a misnomer.
    // After cleanup of Recipe.svelte, this can be removed.
    // Implementation note: to avoid any automatic reactivity, use an array to store the status.
    let initaliseDone = [false];
    function logInitStatus() {
        console.log(`REACTED INITIALISED STATUS=${initaliseDone}`);
    }

    $: {
        if ($isAuthenticated) {
            console.log("~~~~~~~~~~~~~~~INITIALISING BECAUSE AUTHENTICATED~~~~~~~~~~~~~~~~");
            initialiseWithData();
            initaliseDone[0] = true;
        }
    }

    let currentPreviewViewType; // The current preview GUI component to show.
    $roi = {
        left: 0,
        right: 2047,
        top: 0,
        bot: 2047,
    };
    $finalFdkSettings = {
        min_slice: $roi.top,
        max_slice: $roi.bot,
        angle_offset: 0,
        slice_roi: { ...$roi },
    };
    if ($currentSession !== params?.session || $currentTomography !== params?.tomography) {
        console.log(`RESETTING SCAN-SPECIFIC SETTINGS BECAUSE CURRENT SESSION OR TOMOGRAPHY CHANGED!`);
        $currentSession = params?.session;
        $currentTomography = params?.tomography;

        // Reset zoom & pan offset
        $zoom = 1;
        $offset = { x: 0, y: 0 };

        // Reset wide centershift slider zoom
        $centershiftGapStack?.clear();

        // Reset image index values that pertain to a specific scan
        $lastCentershiftPreviewImageIndex = undefined;
        $lastBeamhardCuppingPreviewImageIndex = undefined;
    }
    let previews; // Preview objects from the datapipeline
    let sliceIndex;
    let centershiftSlices = []; // List of all existing slices of type CENTERSHIFT
    let beamhardCuppingSlices = []; // List of all existing slices of type BEAMHARD_CUPPING
    let centershiftSlicePreviews = []; // List of all centershift previews for current slice
    let beamhardCuppingSlicePreviews = []; // List of all beamhard cupping previews for current slice
    let phaseRetrievalSlicePreviews = []; // List of all centershift previews for current slice that should be viewable *inside* the phase retrieval preview view
    let phaseRetrievalDatasetIds = []; // List of currently filtered phase retrievel bases (e.g. [02-tomo-pr00-ph00, 02-tomo-pr00-ph01])
    let finalBeamhardCuppingOrCentershiftSlicePreviews = []; // List of all final beamhard cupping or centershift previews for current slice (normally 1)
    let hasBeamhardCuppingSlicePreviews = false; // Flag to indicate whether beamhard cupping should be egnerated or not
    let allPhaseRetrievalPreviewSettings = []; // List of the phase retrieval settings for the purpose of generating new slices
    let allBeamhardCuppingSlicePreviews = []; // List of all beamhard cupping previews for the current slice (may be used to guide user for which centershift to find data)

    onDestroy(() => {
        // Reset histogram attributes when destroyed
        $histogramThreshold.min = 0;
        $histogramThreshold.max = 255;
    });

    $: {
        logInitStatus();
        console.log(`REACTED ON CHANGED sliceIndex: `, sliceIndex);
        showPreviewIfSliceChanged(sliceIndex);
    }
    $: {
        logInitStatus();
        console.log(`REACTED ON CHANGED FINAL SETTINGS: `, $finalFdkSettings);
        checkAllPreviews($finalFdkSettings);
    }

    $: {
        logInitStatus();
        console.log(`REACTED ON CHANGED phaseRetrievalIds: `, phaseRetrievalDatasetIds);
        filterPreviewsByPhaseRetrievalDatasetId(phaseRetrievalDatasetIds);
        updateSlicePickerAvailableSlices();

        updateSelectedPreview();
    }

    // If centershift has been selected (it should be), only keep those phaseRetrievalSlicePreviews
    // that match the selected centershift
    $: {
        logInitStatus();
        console.log(`REACTED ON CHANGED FINAL SETTINGS CS=${$finalFdkSettings.centershift_object_mm}, TILT=${$finalFdkSettings.tilt_deg}`);
        filterPreviewsByCentershiftAndTilt($finalFdkSettings.centershift_object_mm, $finalFdkSettings.tilt_deg);
        updateSlicePickerAvailableSlices();

        // Find out if there are any beamhard cupping slice previews for the current ph and cs. This is
        // so that WideCentershiftPreview shall know whether to generate BEAMHARD_CUPPING when adding a 
        // (WIDE_)CENTERSHIFT slice.
        if (currentPreviewViewType == 'CENTERSHIFT' || currentPreviewViewType == 'PHASE_RETRIEVAL') {
            let phFilteredPreviews = getPreviewsByPhaseRetrievalDatasetId(previews, phaseRetrievalDatasetIds);
            let tmpBeamhardCuppingSlicePreviews = getPreviewsByType(phFilteredPreviews, "BEAMHARD_CUPPING");
            tmpBeamhardCuppingSlicePreviews = filterPreviewSlicesByCentershiftAndTilt(tmpBeamhardCuppingSlicePreviews);
            hasBeamhardCuppingSlicePreviews = tmpBeamhardCuppingSlicePreviews?.length > 0;
        }

        // Beamhard cupping special handling. beamhardCuppingSlicePreviews should now only contain
        // matching previews, so if we don't have any to show, just set selected preview to undefined
        if (currentPreviewViewType == 'BEAMHARD_CUPPING') {
            if (beamhardCuppingSlicePreviews?.length > 0) {
                console.log('RESETTING BEAMHARD_CUPPING SELECTED PREVIEW TO FIRST');
                $selectedPreview = beamhardCuppingSlicePreviews?.[0];
            } else {
                console.log('RESETTING BEAMHARD_CUPPING SELECTED PREVIEW TO UNDEFINED');
                $selectedPreview = undefined;
            }
        }
    }

    function updateSlicePickerAvailableSlices() {
        // Update slices (used by slice picker)
        if (previews) {
            let phFilteredPreviews = getPreviewsByPhaseRetrievalDatasetId(previews, phaseRetrievalDatasetIds);
            if (phFilteredPreviews) {
                filterAvailableSlices(phFilteredPreviews);
            }
        }
    }

    function updateSelectedPreview() {
        if (currentPreviewViewType == 'PHASE_RETRIEVAL') {
            let availableSlicePreviews = centershiftSlicePreviews;
            // We may have more than one preview in centershiftSlicePreviews. We should prefer the one that matches closest to
            // $selectedPreview (check if centershift matches for instance)
            const currentPreviewSlice = $selectedPreview?.preview_output?.find((s) => s.slice == sliceIndex);
            const matchingPreview = availableSlicePreviews.find((p) =>
                p.preview_output?.find((v) => v.parameter_values[31] == currentPreviewSlice?.parameter_values[31])
            );
            if (matchingPreview && $selectedPreview != matchingPreview) {
                $selectedPreview = matchingPreview;
            } /*else {
                $selectedPreview = availableSlicePreviews[0];
            }*/
        }
    }

    function showCentershiftPreview(ev) {
        if (currentPreviewViewType !== "BEAMHARD_CUPPING" && !$selectedPreview) return; // Ignore if there is no preview to view
        
        currentPreviewViewType = "CENTERSHIFT";
        if (isNumeric(ev.detail.previewIndex)) {
            console.log("STEPPING TO OTHER CENTERSHIFT INDEX: ", ev.detail.previewIndex);
            $selectedPreview = centershiftSlicePreviews[ev.detail.previewIndex];
        } else {
            let selectedPreviewToUse = centershiftSlicePreviews[0];

            /*
            // CODE COMMENTED OUT BECAUSE IT IS ONLY RELEVANT IF WE HAVE BEAMHARD_CUPPING PREVIEWS GENERATED AT THE SAME TIME
            // AS WIDE_CENTERSHIFT, WHICH IS NO LONGER THE TYPICAL CASE IN THE NEW WORKFLOW.
            
            const incomingPreviewPath = extractDatasetName($selectedPreview);
            console.log("------------REGEXP INCOMING ", incomingPreviewPath?.[0]);
            console.log("ABOUT TO MATCH AGAINST THESE PREVIEWS: ", centershiftSlicePreviews);
            let closestPreviewToUse = centershiftSlicePreviews?.find((p) => {
                const outgoingPreviewPath = extractDatasetName(p);
                console.log("------------REGEXP OUTGOING ", outgoingPreviewPath[0]);
                if (outgoingPreviewPath[0] === incomingPreviewPath?.[0]) {
                    console.log("PREVIEW PATH MATCH FOUND: ", outgoingPreviewPath[0]);
                    return true;
                } else {
                    console.log("DISCARDING PREVIEW PATH: ", outgoingPreviewPath[0]);
                }
                return false;
            });
            if (closestPreviewToUse) {
                console.log(
                    "MATCHED THIS PREVIEW WHEN SWITCHING TO ",
                    currentPreviewViewType,
                    ":",
                    closestPreviewToUse
                );
                selectedPreviewToUse = closestPreviewToUse;
            } else {
                console.log(
                    "NO CENTERSHIFT PREVIEW MATCHES CURRENTLY VIEWED PREVIEW BY DATASET NAME: ",
                    incomingPreviewPath?.[0]
                );
            }
            */

            $selectedPreview = selectedPreviewToUse;
        }

        updateMaximumVolume($selectedPreview);
    }

    function showBeamhardCuppingPreview(ev) {
        if (!$selectedPreview) return; // Ignore if there is no preview to view

        currentPreviewViewType = "BEAMHARD_CUPPING";
        if (isNumeric(ev.detail.previewIndex)) {
            console.log("STEPPING TO OTHER BEAMHARD_CUPPING INDEX: ", ev.detail.previewIndex);
            $selectedPreview = beamhardCuppingSlicePreviews[ev.detail.previewIndex];
        } else {
            // Default (naive) behaviour: Just pick the first one in the list of available beamhard cupping previews
            // This will be picked if no other matching method below fnds a better match
            let selectedPreviewToUse = beamhardCuppingSlicePreviews[0];

            /*
            // CODE COMMENTED OUT BECAUSE IT IS ONLY RELEVANT IF WE HAVE BEAMHARD_CUPPING PREVIEWS GENERATED AT THE SAME TIME
            // AS WIDE_CENTERSHIFT, WHICH IS NO LONGER THE TYPICAL CASE IN THE NEW WORKFLOW.

            // Advanced behaviour: Try to match the beamhard cupping preview to the current settings
            // Specifically, a centershift's preview_output.parameter_values[31] should match the centershift
            // that was used in the preview_input.centershift_object_mm setting. WARN: In case of optimisation
            // there is no such value set. Need change in response from API.

            // Idea while no better method exists: compare image_patterns (e.g. same rec0x folder counts as a match).
            // WARNING: Might not work if we come from other than a CENTERSHIFT $selectedPreview
            // WARNING: Assumes that centershift and beamhard cupping previews are generated by the same command,
            // which might not be the case if we ran beamhard cupping preview only (e.g from Swagger).
            // TODO: With new WIDE_CENTERSHIFT workflow, it is indeed the norm that the beamhard cupping
            // previews were not generated by the same command! Would be nice if there was some kind of
            // token set by the server so that we have an easy compare. While we no longer can use the -rec0x
            // trick, we have to come up with something else. Suggesting to use:
            // compare ph0x path AND compare centershift value.
            const incomingPreviewPath = extractDatasetName($selectedPreview);
            console.log("------------REGEXP INCOMING ", incomingPreviewPath?.[0]);
            console.log("ABOUT TO MATCH AGAINST THESE PREVIEWS: ", beamhardCuppingSlicePreviews);
            let closestPreviewToUse = beamhardCuppingSlicePreviews?.find((p) => {
                const outgoingPreviewPath = extractDatasetName(p);
                console.log("------------REGEXP OUTGOING ", outgoingPreviewPath[0]);
                if (outgoingPreviewPath[0] === incomingPreviewPath?.[0]) {
                    console.log("PREVIEW PATH MATCH FOUND: ", outgoingPreviewPath[0]);
                    return true;
                } else {
                    console.log("DISCARDING PREVIEW PATH: ", outgoingPreviewPath[0]);
                }
                return false;
            });
            if (closestPreviewToUse) {
                console.log(
                    "MATCHED THIS PREVIEW WHEN SWITCHING TO ",
                    currentPreviewViewType,
                    ":",
                    closestPreviewToUse
                );
                selectedPreviewToUse = closestPreviewToUse;
            } else {
                console.log(
                    "NO BEAMHARD_CUPPING PREVIEW MATCHES CURRENTLY VIEWED PREVIEW BY DATASET NAME: ",
                    incomingPreviewPath?.[0]
                );
            }
            */

            $selectedPreview = selectedPreviewToUse;
        }
    }

    function showPhaseRetrievalPreview(ev) {
        if (currentPreviewViewType !== "BEAMHARD_CUPPING" && !$selectedPreview) return; // Ignore if there is no preview to view

        currentPreviewViewType = "PHASE_RETRIEVAL";
        console.log(":::::::STEPPING THROUGH PHASE RETRIEVAL PREVIEWS! ");
        if (isNumeric(ev.detail.previewIndex)) {
            console.log("STEPPING TO OTHER CENTERSHIFT INDEX: ", ev.detail.previewIndex);
            $selectedPreview = phaseRetrievalSlicePreviews[ev.detail.previewIndex];
        } else {
            // By default, just show the current centershift preview. Otherwise, pick the first legal one
            // i.e. where the phase retrieval filter matches
            if ($selectedPreview?.preview_input?.preview_input?.preview_type != "CENTERSHIFT" && $selectedPreview?.preview_input?.preview_input?.preview_type != "WIDE_CENTERSHIFT") {
                let availableSlicePreviews = getPreviewsByPhaseRetrievalDatasetId(phaseRetrievalSlicePreviews, phaseRetrievalDatasetIds);
                $selectedPreview = availableSlicePreviews[0];
            }
        }

        updateMaximumVolume($selectedPreview, sliceIndex);
    }

    let finalPreviewType;
    function showFinalPreview() {
        if (currentPreviewViewType !== "BEAMHARD_CUPPING" && !$selectedPreview) return; // Ignore if there is no preview to view

        currentPreviewViewType = "FINAL";

        // Final preview can be either BEAMHARD_CUPPING or (WIDE_)CENTERSHIFT
        if (beamhardCuppingSlicePreviews?.length > 0) {
            // If we have BEAMHARD_CUPPING previews for this slice, the final view should show this 
            finalBeamhardCuppingOrCentershiftSlicePreviews = beamhardCuppingSlicePreviews;
            finalPreviewType = "BEAMHARD_CUPPING";
        } else if (centershiftSlicePreviews?.length > 0) {
            // If we have no BEAMHARD_CUPPING previews, the final view should show (WIDE_)CENTERSHIFT previews instead
            finalBeamhardCuppingOrCentershiftSlicePreviews = centershiftSlicePreviews;
            finalPreviewType = "CENTERSHIFT";
        } else {
            finalPreviewType = undefined;
        }

        // Default (naive) behaviour: Just pick the first one in the list of available beamhard cupping previews
        // This will be picked if no other matching method below finds a better match
        let selectedPreviewToUse = finalBeamhardCuppingOrCentershiftSlicePreviews[0];

        /*
        // CODE COMMENTED OUT BECAUSE IT IS ONLY RELEVANT IF WE HAVE BEAMHARD_CUPPING PREVIEWS GENERATED AT THE SAME TIME
        // AS WIDE_CENTERSHIFT, WHICH IS NO LONGER THE TYPICAL CASE IN THE NEW WORKFLOW.

        // Idea while no better method exists: compare image_patterns (e.g. same rec0x folder counts as a match).
        // WARNING: Might not work if we come from other than a CENTERSHIFT $selectedPreview
        // WARNING: Assumes that centershift and beamhard cupping previews are generated by the same command,
        // which might not be the case if we ran beamhard cupping preview only (e.g from Swagger).
        const incomingPreviewPath = extractDatasetName($selectedPreview);
        console.log("------------REGEXP INCOMING ", incomingPreviewPath?.[0]);
        console.log("ABOUT TO MATCH AGAINST THESE PREVIEWS: ", finalBeamhardCuppingOrCentershiftSlicePreviews);
        let closestPreviewToUse = finalBeamhardCuppingOrCentershiftSlicePreviews?.find((p) => {
            const outgoingPreviewPath = extractDatasetName(p);
            console.log("------------REGEXP OUTGOING ", outgoingPreviewPath[0]);
            if (outgoingPreviewPath[0] === incomingPreviewPath?.[0]) {
                console.log("PREVIEW PATH MATCH FOUND: ", outgoingPreviewPath[0]);
                return true;
            } else {
                console.log("DISCARDING PREVIEW PATH: ", outgoingPreviewPath[0]);
            }
            return false;
        });
        if (closestPreviewToUse) {
            console.log(
                "MATCHED THIS PREVIEW WHEN SWITCHING TO ",
                currentPreviewViewType,
                ":",
                closestPreviewToUse
            );
            selectedPreviewToUse = closestPreviewToUse;
        } else {
            console.log(
                "NO FINAL (BEAMHARD_CUPPING) PREVIEW MATCHES CURRENTLY VIEWED PREVIEW BY DATASET NAME: ",
                incomingPreviewPath?.[0]
            );
        }
        */

        finalBeamhardCuppingOrCentershiftSlicePreviews = selectedPreviewToUse ? [selectedPreviewToUse] : [];
        $selectedPreview = selectedPreviewToUse;

        updateMaximumVolume($selectedPreview);
    }

    /**
     * Finds a slice (sets selectedPreview) on sliceIndex out of all matching parameters.
     * If currentPreviewViewType != PHASE_RETRIEVAL, then filter type-specific subsets
     * (centershiftSlicePreviews and beamhardCuppingSlicePreviews) that match phaseRetrievalDatasetIds.
     *
     * If currentPreviewViewType == PHASE_RETRIEVAL, then select a CENTERSHIFT preview that matches phaseRetrievalDatasetIds,
     * but don't modify subset (phaseRetrievalSlicePreviews).
     *
     * @param sliceIndex
     */
    function showPreviewIfSliceChanged(sliceIndex) {
        console.log("SLICE CHANGED TO ", sliceIndex);
        if (!previews) {
            currentPreviewViewType = "CENTERSHIFT";
            return;
        }

        // Since there are never any slices with type PHASE_RETRIEVAL, we filter on CENTERSHIFT
        let filterOnPreviewViewType = currentPreviewViewType;
        if (currentPreviewViewType === "PHASE_RETRIEVAL") {
            filterOnPreviewViewType = "CENTERSHIFT";
        } else if (currentPreviewViewType === "FINAL") {
            if (beamhardCuppingSlices && beamhardCuppingSlices.length > 0)
                filterOnPreviewViewType = "BEAMHARD_CUPPING";
            else
                filterOnPreviewViewType = "CENTERSHIFT";
        }

        let phFilteredPreviews = getPreviewsByPhaseRetrievalDatasetId(previews, phaseRetrievalDatasetIds);
        let availableSlices = getAllSlices(getPreviewsByType(phFilteredPreviews, filterOnPreviewViewType));

        // Find out if there are any beamhard cupping slice previews for the current ph and cs. This is
        // so that WideCentershiftPreview shall know whether to generate BEAMHARD_CUPPING when adding a 
        // (WIDE_)CENTERSHIFT slice.
        let tmpBeamhardCuppingSlicePreviews = getPreviewsByType(phFilteredPreviews, "BEAMHARD_CUPPING");
        tmpBeamhardCuppingSlicePreviews = filterPreviewSlicesByCentershiftAndTilt(tmpBeamhardCuppingSlicePreviews);
        hasBeamhardCuppingSlicePreviews = tmpBeamhardCuppingSlicePreviews?.length > 0;
        //DEBUG: console.log("@@@@@@@CENTERSHIFT HAS BEAMHARD CUPPING SLICE PREVIEWS", hasBeamhardCuppingSlicePreviews);

        if (!availableSlices.includes(sliceIndex)) {
            //currentPreviewViewType = "CENTERSHIFT";
            centershiftSlicePreviews = [];
            beamhardCuppingSlicePreviews = [];
            allBeamhardCuppingSlicePreviews = [];
            console.log(`NO ${currentPreviewViewType} PREVIEW AVAILABLE FOR SLICE INDEX `, sliceIndex);

            $selectedPreview = undefined;
        } else {
            centershiftSlicePreviews = getCentershiftPreviewsBySlice(phFilteredPreviews, sliceIndex);
            allBeamhardCuppingSlicePreviews = getBeamhardCuppingPreviewsBySlice(phFilteredPreviews, sliceIndex);
            beamhardCuppingSlicePreviews = filterPreviewSlicesByCentershiftAndTilt(allBeamhardCuppingSlicePreviews);

            phaseRetrievalSlicePreviews = getCentershiftPreviewsBySlice(previews, sliceIndex); // Phase retrieval view shows centershift images
            if (currentPreviewViewType === "CENTERSHIFT") {
                // We may have more than one preview in centershiftSlicePreviews. We should prefer the one that matches closest to
                // $selectedPreview (check if centershift matches for instance)
                const currentPreviewSlice = $selectedPreview?.preview_output?.find((s) => s.slice == sliceIndex);
                console.log("CURRENT PREVIEW SLICE DATASET = ", currentPreviewSlice);
                const currentPreviewOutputIndex = $selectedPreview?.preview_output?.findIndex(s => s.slice == sliceIndex) || 0;
                const matchingPreviews = centershiftSlicePreviews.filter((p) =>
                    p.preview_output?.find((v) => {
                        const centershiftIndexRange = $selectedPreview?.preview_output?.[currentPreviewOutputIndex]?.parameter_values?.length;
                        const centershiftMiddleIndex = Math.floor((centershiftIndexRange - 1) / 2);
                        return v.parameter_values[centershiftMiddleIndex] == currentPreviewSlice?.parameter_values[centershiftMiddleIndex];
                    })
                );
                console.log("MATCHING PREVIEWS = ", matchingPreviews);
                const matchingPreview = matchingPreviews.find((p) => {
                    return (p.preview_input.generic_preview_input.reconstruction_settings.tilt_deg?.toPrecision(3) == $selectedPreview?.preview_input.generic_preview_input.reconstruction_settings.tilt_deg?.toPrecision(3));
                });
                console.log("MATCHING PREVIEW = ", matchingPreview);
                if (matchingPreview) {
                    $selectedPreview = matchingPreview;
                } else {
                    $selectedPreview = centershiftSlicePreviews[0];
                }
            } else if (currentPreviewViewType === "BEAMHARD_CUPPING") {
                // Select the closest matching beamhard_cupping preview by checking centershift value. Warning! Perhaps this may fail
                // if we have multiple BEAMHARD_CUPPING previews with the same cenetrshift. TODO: check tilt etc. too.
                const currentCentershift =
                    $selectedPreview?.preview_input?.generic_preview_input?.reconstruction_settings
                        ?.centershift_object_mm;
                const matchingPreview = beamhardCuppingSlicePreviews.find(
                    (p) =>
                        p.preview_input?.generic_preview_input?.reconstruction_settings?.centershift_object_mm ==
                        currentCentershift
                );
                if (matchingPreview) {
                    $selectedPreview = matchingPreview;
                } else {
                    $selectedPreview = beamhardCuppingSlicePreviews[0];
                }
            } else if (currentPreviewViewType === "PHASE_RETRIEVAL") {
                let availableSlicePreviews = getCentershiftPreviewsBySlice(centershiftSlicePreviews, sliceIndex);
                // We may have more than one preview in centershiftSlicePreviews. We should prefer the one that matches closest to
                // $selectedPreview (check if centershift matches for instance)
                const currentPreviewSlice = $selectedPreview?.preview_output?.find((s) => s.slice == sliceIndex);
                const matchingPreview = availableSlicePreviews.find((p) =>
                    p.preview_output?.find((v) => v.parameter_values[31] == currentPreviewSlice?.parameter_values[31])
                );
                if (matchingPreview) {
                    $selectedPreview = matchingPreview;
                } else {
                    $selectedPreview = availableSlicePreviews[0];
                }
            } else if (currentPreviewViewType === "FINAL") {
                // Select the closest matching beamhard_cupping preview by checking centershift value. Warning! Perhaps this may fail
                // if we have multiple BEAMHARD_CUPPING previews with the same cenetrshift. TODO: check tilt etc. too.
                // TODO: With wide centershift workflow, the final preview view may show centershift previews, so don't assume
                // we have beamhard cupping previews.
                const currentCentershift =
                    $selectedPreview?.preview_input?.generic_preview_input?.reconstruction_settings
                        ?.centershift_object_mm;
                const matchingPreview = finalBeamhardCuppingOrCentershiftSlicePreviews.find(
                    (p) =>
                        p.preview_input?.generic_preview_input?.reconstruction_settings?.centershift_object_mm ==
                        currentCentershift
                );
                if (matchingPreview) {
                    $selectedPreview = matchingPreview;
                } else {
                    $selectedPreview = finalBeamhardCuppingOrCentershiftSlicePreviews[0];
                }
            }

            if (currentPreviewViewType !== "FINAL") {
                updateMaximumVolume($selectedPreview, sliceIndex);
            }
        }
    }

    /**
     * Preview handling helper functions
     */
    function getPreviewsByType(previews, type) {
        // Type can be CENTERSHIFT, BEAMHARD_CUPPING
        const typePreviews = previews?.filter((p) => {
            // Special handling when preview is WIDE_CENTERSHIFT and user view is centershift. Keep as centershift
            if (p.preview_input?.preview_input?.preview_type == "WIDE_CENTERSHIFT" && type == "CENTERSHIFT") return true;
            
            return p.preview_input?.preview_input?.preview_type == type;
        });
        console.log("PREVIEWS OF TYPE ", type);
        console.log(typePreviews);
        return typePreviews;
    }

    function getAllSlices(previews) {
        const slices = previews?.map((p) => {
            return p?.preview_input?.generic_preview_input?.slices;
        });
        console.log("ALL SLICES: ");
        const flattenedSlices = slices?.flat();
        console.log(flattenedSlices);
        return flattenedSlices;
    }

    function filterAvailableSlices(previews) {
        centershiftSlices = getAllSlices(getPreviewsByType(previews, "CENTERSHIFT"));
        console.log("ALL AVAILABLE CENTERSHIFT SLICES: ", centershiftSlices);
        let tmpBeamhardCuppingSlicePreviews = getPreviewsByType(previews, "BEAMHARD_CUPPING");
        console.log("ALL AVAILABLE BEAMHARD_CUPPING SLICES: ", beamhardCuppingSlices);
        beamhardCuppingSlicePreviews = filterPreviewSlicesByCentershiftAndTilt(tmpBeamhardCuppingSlicePreviews);
        beamhardCuppingSlices = getAllSlices(beamhardCuppingSlicePreviews);
    }

    // Return all centershift previews that include a slice at the given sliceIndex
    function getCentershiftPreviewsBySlice(previews, sliceIndex) {
        let centershiftPreviews = getPreviewsByType(previews, "CENTERSHIFT");
        const centershiftPreviewsForSlice = centershiftPreviews.filter((p) => {
            return p?.preview_output?.find((v) => v.slice === sliceIndex);
        });
        return centershiftPreviewsForSlice;
    }

    function getBeamhardCuppingPreviewsBySlice(previews, sliceIndex) {
        console.log(`FILTERING ${previews?.length} PREVIEWS (BEAMHARD_CUPPING OR OTHER) BY SLICE ${sliceIndex}`);
        let beamhardCuppingPreviews = getPreviewsByType(previews, "BEAMHARD_CUPPING");
        const beamhardCuppingPreviewsForSlice = beamhardCuppingPreviews.filter((p) => {
            return p?.preview_output?.find((v) => v.slice === sliceIndex);
        });

        console.log(`FILTERED ${previews?.length} BEAMHARD_CUPPING PREVIEWS BY SLICE ${sliceIndex}`);
        return beamhardCuppingPreviewsForSlice;

        /*
        // New behaviour with WIDE_CENTERSHIFT workflow: Only keep those previews that have
        // the current centershift value
        const centershiftValue = ($finalFdkSettings?.centershift_object_mm * 1000
            || $selectedPreview?.preview_input?.generic_preview_input?.reconstruction_settings?.centershift_object_mm * 1000).toFixed(0) / 1000;

        const centershiftFilteredBeamhardCuppingPreviewsForSlice = beamhardCuppingPreviewsForSlice?.filter(p => {
            if (!centershiftValue) return true;

            const centershiftMicrometers = p?.preview_input?.generic_preview_input?.reconstruction_settings?.centershift_object_mm * 1000;
            const isWithinCs = (centershiftValue * 1000).toFixed(0) == centershiftMicrometers.toFixed(0);

            isWithinCs
                ? console.log(`*****KEEPING BEAMHARD_CUPPING SLICE ${sliceIndex}`, p)
                : console.log(
                    `*****DISCARDING BEAMHARD_CUPPING ${p} SLICE ${sliceIndex} because ${centershiftValue * 1000} not same as ${centershiftMicrometers} [μm]`
                );
            
            // Accept if any criteron is true
            const keep = isWithinCs;
            return keep;
        });

        return centershiftFilteredBeamhardCuppingPreviewsForSlice;
        */
    }

    function getPreviewsByPhaseRetrievalDatasetId(previews, phaseRetrievalDatasetIds) {
        console.log("€€€€€€€€€€€€€€€€€€€€€€€€€€€€ PH FILTER = ", phaseRetrievalDatasetIds);
        const previewsMatchingPhaseRetrievalDatasetId = previews?.filter((p) => {
            const base = extractPhaseRetrievalBaseFromPath(p?.preview_output?.[0]?.large_image_pattern);
            // If, for some reason (bug), we have no phase retrieval base, just exclude it
            if (!base || base == "") {
                return false;
            }
            // If the phase retrieval dataset ids filter is missing/empty, accept all previews
            if (!phaseRetrievalDatasetIds || phaseRetrievalDatasetIds?.length == 0) {
                return true;
            }
            return phaseRetrievalDatasetIds?.includes(base);
        });
        return previewsMatchingPhaseRetrievalDatasetId;
    }

    function filterPreviewsByPhaseRetrievalDatasetId(phaseRetrievalDatasetIds) {
        if (!previews) {
            return;
        }
        centershiftSlicePreviews = getCentershiftPreviewsBySlice(previews, sliceIndex);
        centershiftSlicePreviews = getPreviewsByPhaseRetrievalDatasetId(
            centershiftSlicePreviews,
            phaseRetrievalDatasetIds
        );
        let allBeamhardCuppingSlicePreviews = getBeamhardCuppingPreviewsBySlice(previews, sliceIndex);
        allBeamhardCuppingSlicePreviews = getPreviewsByPhaseRetrievalDatasetId(
            allBeamhardCuppingSlicePreviews,
            phaseRetrievalDatasetIds
        );
        beamhardCuppingSlicePreviews = filterPreviewSlicesByCentershiftAndTilt(allBeamhardCuppingSlicePreviews);
        console.log("=====FILTER PHASE RETRIEVAL SET: ", phaseRetrievalDatasetIds);
        console.log("=====CENTERSHIFT SLICE PREVIEWS SEEN BY RECIPE: ", centershiftSlicePreviews);
        console.log("=====BEAMHARD CUPPING SLICE PREVIEWS SEEN BY RECIPE: ", beamhardCuppingSlicePreviews);
    }

    function filterPreviewSlicesByCentershiftAndTilt(slicePreviews) {
        const centershiftValue = ($finalFdkSettings?.centershift_object_mm * 1000
            || $selectedPreview?.preview_input?.generic_preview_input?.reconstruction_settings?.centershift_object_mm * 1000).toFixed(0) / 1000;

        const tiltValue = ($finalFdkSettings?.tilt_deg * 1000
            || $selectedPreview?.preview_input?.generic_preview_input?.reconstruction_settings?.tilt_deg * 1000).toFixed(0) / 1000;
        
        let filteredSlicePreviews = slicePreviews?.filter((p) => {
            const previewType = p?.preview_input?.preview_input?.preview_type;

            // Check if the phase retrieval preview has the same centershift value as the current settings from the centershift preview.
            // NOTE: Since we know that the centershift value from the UI is a rounded value, we need to do the same with the
            // selected preview's centershift value before we compare.

            const centershiftMicrometers =
                p?.preview_input?.generic_preview_input?.reconstruction_settings?.centershift_object_mm * 1000;
            const tiltMillidegrees =  p?.preview_input?.generic_preview_input?.reconstruction_settings?.tilt_deg * 1000;

            let isSameInput = false;
            if (isNumeric(tiltMillidegrees) && isNumeric(tiltValue)) {
                isSameInput = centershiftMicrometers.toFixed(0) == (centershiftValue * 1000).toFixed(0) && tiltMillidegrees.toFixed(0) == (tiltValue * 1000).toFixed(0);
            } else {
                isSameInput = centershiftMicrometers.toFixed(0) == (centershiftValue * 1000).toFixed(0);
            }

            // NOTE: prettier does not play nice with eslint, since prettier insert 2 extra hard-coded spaces (even if tabWith = 4)
            // when line-breaking ternary operations, while eslint expectseverything to be a multiple of tabWidth
            // prettier-ignore
            if (isNumeric(tiltMillidegrees) && isNumeric(tiltValue)) {
                isSameInput
                    ? console.log("*****KEEPING ", p)
                    : console.log(
                        `*****DISCARDING ${p} because (existing) ${centershiftMicrometers.toFixed(0)} != ${(centershiftValue * 1000)?.toFixed(0)} [μm] (wanted) or (existing) ${tiltMillidegrees.toFixed(0)} != ${(tiltValue * 1000)?.toFixed(0)} [m°] (wanted)`
                    );
            } else {
                isSameInput
                    ? console.log("*****KEEPING ", p)
                    : console.log(
                        `*****DISCARDING ${p} because (existing) ${centershiftMicrometers.toFixed(0)} != ${(centershiftValue * 1000)?.toFixed(0)} [μm] (wanted). Ignoring tilt because NaN.`
                    );
            }
            
            let keep = isSameInput;
            
            
            // TODO: If the selected preview (could be beamhard cupping) has centershift value != input value,
            // of the centershift preview we are looking at in this comparator,
            // it will not match and thus discarded. Phase retrieval previews may get incorrect count.
            // Find another way!
            // Attempt 1: Check if the selected preview's center value is within the range of this centershift preview
            if (previewType == 'CENTERSHIFT' || previewType == 'WIDE_CENTERSHIFT' ) {
                const minCs = p?.preview_output?.[0]?.parameter_values?.[0];
                const maxCs = p?.preview_output?.[0]?.parameter_values?.[p.preview_output?.[0].parameter_values.length - 1];
                const isWithinCs = centershiftValue >= minCs && centershiftValue < maxCs;

                isWithinCs
                    ? console.log(`*****KEEPING ${previewType}`, p)
                    : console.log(
                        `*****DISCARDING ${previewType} because ${centershiftValue * 1000} not within ${minCs * 1000}..${maxCs * 1000} [μm]`
                    );

                keep = keep || isWithinCs;
            }

            return keep;
        });

        return filteredSlicePreviews; 
    }

    function filterPreviewsByCentershiftAndTilt(centershiftValue, tiltValue) {
        if (!previews) {
            return;
        }
        if (!isNumeric(centershiftValue)) {
            return;
        }
        /*if (!isNumeric(tiltValue)) {
            return;
        }*/

        phaseRetrievalSlicePreviews = getCentershiftPreviewsBySlice(previews, sliceIndex);

        phaseRetrievalSlicePreviews = filterPreviewSlicesByCentershiftAndTilt(phaseRetrievalSlicePreviews);

        // Filter beamhard cupping previews by slice (TODO: remove side effect where this function considers cs and tilt)
        beamhardCuppingSlicePreviews = getBeamhardCuppingPreviewsBySlice(previews, sliceIndex);

        // Filter by phase retrieval
        console.log(`FILTERING BEAMHARD_CUPPING ${beamhardCuppingSlicePreviews?.length} SLICE PREVIEWS BY PH IDS:`, phaseRetrievalDatasetIds);
        beamhardCuppingSlicePreviews = getPreviewsByPhaseRetrievalDatasetId(beamhardCuppingSlicePreviews, phaseRetrievalDatasetIds);

        // TODO: getBeamhardCuppingPreviewsBySlice above does almost exactly the same as filterPreviewSlicesByCentershiftAndTilt.
        // Merge into one function.

        beamhardCuppingSlicePreviews = filterPreviewSlicesByCentershiftAndTilt(beamhardCuppingSlicePreviews);
    }

    function setInitialCameraRoi(newRoi) {
        $roi = newRoi;
    }

    function setInitialSliceRoi(newRoi, min_slice, max_slice) {
        if (!$maximumVolume) {
            $maximumVolume = {};
        }
        // If roi is updated, also update the final settings min_slice and max_slice
        // as well as the pixel roi
        if (newRoi?.top && !isNaN(newRoi.top)) $finalFdkSettings.min_slice = min_slice;
        $maximumVolume.min_slice = min_slice;
        if (newRoi.bot && !isNaN(newRoi.bot)) {
            $finalFdkSettings.max_slice = max_slice;
            $maximumVolume.max_slice = max_slice;
        }
        if (newRoi && !isNaN(newRoi.left) && !isNaN(newRoi.right)) {
            $finalFdkSettings.slice_roi = {
                ...newRoi,
            };
            $maximumVolume.slice_roi = {
                ...newRoi,
            };
        }
    }

    function isNumeric(num) {
        return (typeof num === "number" || (typeof num === "string" && num.trim() !== "")) && !isNaN(num);
    }

    let checked = [
        // { type: 'CENTERSHIFT', preview: {...}}
    ];

    function isChecked(checked, previewType) {
        //console.log('CHECKING IF ', previewType, ' IS CHECKED...');
        let found = false;
        if (checked?.find((c) => c?.type === previewType)) found = true;
        //console.log('...', found);
        return found;
    }

    function check(previewType) {
        let checkedType = checked?.find((c) => c?.type === previewType);
        if (!checkedType) {
            checked.push({ type: previewType });
        }

        checked = checked;
    }

    function uncheck(previewType) {
        let checkedIndex = checked?.findIndex((c) => c?.type === previewType);
        if (checkedIndex > -1) {
            checked.splice(checkedIndex, 1);
        }

        checked = checked;
    }

    function isPhaseRetrievalPreviewAvailable(checked) {
        if (isChecked(checked, "CENTERSHIFT")) return true;
        return false;
    }

    function isBeamhardCuppingPreviewAvailable(checked) {
        if (isChecked(checked, "CENTERSHIFT") && isChecked(checked, "PHASE_RETRIEVAL")) return true;
        return false;
    }

    function isFinalPreviewAvailable(checked) {
        if (isChecked(checked, "CENTERSHIFT") && isChecked(checked, "PHASE_RETRIEVAL") && isChecked(checked, "BEAMHARD_CUPPING")) return true;
        return false;
    }

    function checkAllPreviews(fdkSettings) {
        if (!fdkSettings) return;
        if (!isNumeric(fdkSettings.centershift_object_mm)) {
            uncheck("CENTERSHIFT");
        } else {
            check("CENTERSHIFT");
        }
        if (!isNumeric(fdkSettings.beamhard_cupping)) {
            uncheck("BEAMHARD_CUPPING");
        } else {
            check("BEAMHARD_CUPPING");
        }
        if (!fdkSettings.phase_retrieval_dataset_id) {
            uncheck("PHASE_RETRIEVAL");
        } else {
            check("PHASE_RETRIEVAL");
        }
    }

    function getActualCurrentPreviewType(currentPreviewViewType) {
        if (currentPreviewViewType == "FINAL") {
            switch (finalPreviewType) {
                case "BEAMHARD_CUPPING":
                    return "BEAMHARD_CUPPING";
                default:
                    return "CENTERSHIFT";
            }
        }

        if (currentPreviewViewType == "BEAMHARD_CUPPING") return "BEAMHARD_CUPPING";

        return "CENTERSHIFT";
    }

    function getLatestPhaseRetrievalBase(previews) {
        // Extract the phase retrieval bases for all found previews that match settings
        let phaseRetrievalBases = previews?.map((p) => {
            return extractLatestPhaseRetrievalBaseAndTime(p); // Returns { base: , startTime: }
        });
        let sortedPhBases = [...phaseRetrievalBases];
        sortedPhBases?.sort(timeComparator);
        let finalSortedPhBases = sortedPhBases.map((b) => b.base);
        finalSortedPhBases = [...new Set(finalSortedPhBases)]; // Remove duplicates
        return finalSortedPhBases?.slice(0, 1);
    }

    function getLowestPhaseRetrievalBase(previews) {
        // Extract the phase retrieval bases for all found previews that match settings
        let phaseRetrievalBases = previews?.map((p) => {
            return extractLatestPhaseRetrievalBaseAndTime(p); // Returns { base: , startTime: , delta: , mu: }
        });
        let sortedPhBases = [...phaseRetrievalBases];
        sortedPhBases?.sort(phaseRetrievalComparator);
        let finalSortedPhBases = sortedPhBases.map((b) => b.base);
        finalSortedPhBases = [...new Set(finalSortedPhBases)]; // Remove duplicates
        return finalSortedPhBases?.slice(0, 1);
    }

    // Collect all phase retrieval settings in one list
    function getAllPhaseRetrievalPreviewSettings(previews) {
        // Extract the phase retrieval bases for all found previews that match settings
        let phaseRetrievals = previews?.map((p) => {
            return extractLatestPhaseRetrievalBaseAndTime(p); // Returns { base: , startTime: , delta: , mu: }
        });
        // Sort by phase retrieval quotient (lowest delta/mu first)
        let sortedPhs = [...phaseRetrievals];
        sortedPhs?.sort(phaseRetrievalComparator);
        // Extract delta/mu settings
        let dedupedSortedPhBases = [];
        let dedupedSortedPhs = [];
        sortedPhs.forEach((b) => {
            if (!dedupedSortedPhBases.includes(b.base)) {
                dedupedSortedPhBases.push(b.base);
                dedupedSortedPhs.push(b);
            }
        });
        console.log('ALL DEDUPED SORTED PH SETTINGS:', dedupedSortedPhs);
        return dedupedSortedPhs;
    }

    /**
     * Update the maximum volume information (slices, width, breadth). 
     * Each preview may define its own maximum volume, so this should be called
     * whenever we change preview. In fact, the information comes on each slice
     * from the coordinator API, so it might have to be updated whenever we change
     * slice.
     * @param preview e.g. {preview_input, preview_output: []}
     * @param slice e.g. 256
     */
    function updateMaximumVolume(preview, slice) {
        if (!preview || !preview.preview_output) return;
        let matchingSliceIndex = preview.preview_output.findIndex(s => s.slice === slice);
        let sliceIndex = matchingSliceIndex > -1 ? matchingSliceIndex : 0;
        const maximumVolumeRoi = preview?.preview_output?.[sliceIndex]?.maximum_volume?.slice_roi;
        const min_slice = preview?.preview_output?.[sliceIndex]?.maximum_volume?.min_slice;
        const max_slice = preview?.preview_output?.[sliceIndex]?.maximum_volume?.max_slice;

        if (maximumVolumeRoi) {
            const newRoi = { ...maximumVolumeRoi };
            setInitialSliceRoi(newRoi, min_slice, max_slice);
        }
    }

    /**
     * When page is mounted, load relevant data from datapipeline API, extract into state variables and show
     * the first preview. This function is also called when the recipe page is refreshed (i.e. typically
     * when new data is available).
     */
    async function initialiseWithData() {
        // First, load details about the tomography (e.g. roi and defaultParameters)
        let tomographyDetails;
        const tomographyDetailsApiResponse = await getTomographyDetailsApi(
            $hostname,
            $isAuthenticated,
            $stack,
            params.session,
            params.tomography
        );
        if (isNumeric(tomographyDetailsApiResponse)) {
            let errorMsg;
            switch (tomographyDetailsApiResponse) {
                case 401:
                    errorMsg = "Not authorised to read";
                    break;
                case 503:
                    errorMsg = "Service unavailable";
                    break;
                default:
                    errorMsg = tomographyDetailsApiResponse;
            }
            let msg = `${now()}<br/>Tomography details API error: ${errorMsg}`;
            toast.push(msg, toastThemeError);

            return; // Not logged in or server error, don't load data
        } else {
            let msg = `${now()}<br/>Fetched tomography OK`;
            toast.push(msg, toastThemeSuccess);
            tomographyDetails = tomographyDetailsApiResponse;
        }

        const cameraRoi = tomographyDetails?.command?.camera?.roi_px;
        const maximumVolumeRoi = tomographyDetails?.maximum_volume?.slice_roi;
        const min_slice = tomographyDetails?.maximum_volume?.min_slice;
        const max_slice = tomographyDetails?.maximum_volume?.max_slice;

        // Default roi bounds based on camera. Only works if we don't have offset image, so
        // only useful as a fallback in case we don't have maximum_volume.
        // Rescale to new roi bounds counting from zero (disregard cropped)
        let newRoi = {
            left: cameraRoi.left,
            right: cameraRoi.right,
            top: 0,
            bot: cameraRoi.bot - cameraRoi.top,
        };
        setInitialCameraRoi(newRoi);

        // If we have maximumVolume info, use this instead
        if (maximumVolumeRoi) {
            newRoi = { ...maximumVolumeRoi };
            setInitialSliceRoi(newRoi, min_slice, max_slice);
        }

        tomography = tomographyDetails; // We store this so that SlicePicker can use it

        const previewDetails = await getGroupedPreviewDetailsApi(
            $hostname,
            $isAuthenticated,
            $stack,
            params.session,
            params.tomography
        );
        if (previewDetails) {
            let unsortedPreviews = previewDetails.previews;
            console.log("LOADED (UNSORTED) PREVIEWS: ", unsortedPreviews);

            // ---------- Discard legacy CENTERSHIFT.
            // First go through all previews and look for legacy CENTERSHIFT
            let legacyFilteredUnsortedPreviews = unsortedPreviews;
            const legacyRecDatasetBases = [];
            unsortedPreviews.forEach((p) => {
                if (p.preview_input.preview_input.preview_type == "CENTERSHIFT") {
                    const recDatasetBase = extractDatasetNameFromPath(p.preview_output?.[0]?.large_image_pattern)?.[0];
                    if (recDatasetBase && !legacyRecDatasetBases.includes(recDatasetBase)) {
                        legacyRecDatasetBases.push(recDatasetBase);
                    }
                }
            });
            // DEBUG: console.log('LEGACY REC DATSET BASES: ', legacyRecDatasetBases);

            // Then prune away any preview with the same -recXY dataset name
            // This should ge rid of both CENTERSHIFT and BEAMHARD_CUPPING previews
            // that were generated at the same time (according to legacy workflow).
            legacyFilteredUnsortedPreviews = unsortedPreviews.filter((p) => {
                const recDatasetBase = extractDatasetNameFromPath(p.preview_output?.[0]?.large_image_pattern)?.[0];
                if (recDatasetBase && legacyRecDatasetBases.includes(recDatasetBase)) return false;
                else return true;
            });
            unsortedPreviews = legacyFilteredUnsortedPreviews;
            // ---------- END Discard legacy CENTERSHIFT

            // The resulting set of previews should now have no WIDE_CENTERSHIFT previews left
            console.log("LOADED (WIDE-FILTERED UNSORTED) PREVIEWS: ", unsortedPreviews);

            // To find the latest phase retrieval base, we should disregard beamhard cupping previews
            let unsortedCentershiftPreviews = unsortedPreviews?.filter(p => p.preview_input.preview_input.preview_type == "WIDE_CENTERSHIFT");

            // Keep the latest preprocessing tree only
            let phBase = getLatestPhaseRetrievalBase(unsortedCentershiftPreviews);
            console.log("€€€€€€€€€€€€€€€€€€€ LATEST PH BASE: ", phBase);
            if ($selectedPreview && $finalFdkSettings?.phase_retrieval_dataset_id?.length > 0) {
                // If we are currently viewing a preview, or waiting for one, changing phase retrieval to the latest
                // would be confusing to the user. Therefore, stay in the current view even if we get new data
            } else {
                if (currentPreviewViewType !== "BEAMHARD_CUPPING") {
                    console.log('€€€€€€€€€€€€€€€€€€€ SETTING PHDATASETIDS BECAUSE NO SELECTEDPREVIEW: ', phaseRetrievalDatasetIds, ' --> ', phBase);
                    phaseRetrievalDatasetIds = phBase;
                } else {
                    // If we are waiting for a beamhard cupping preview, it might be generated for something that is not the latest phBase
                    console.log('€€€€€€€€€€€€€€€€€€€ KEEPING PHDATASETIDS AS IS BECAUSE WAITING FOR BEAMHARD CUPPING: ', phaseRetrievalDatasetIds, ' -/- ', phBase);
                }
            }
            
            const preprocessingBase = extractPreprocessingBaseFromPath(phBase?.[0]);

            const onlyLatestPreprocessingPreviews = unsortedPreviews.filter((p) => {
                const base = extractPhaseRetrievalBaseFromPath(p?.preview_output?.[0]?.large_image_pattern);
                const pPreprocessingBase = extractPreprocessingBaseFromPath(base);
                if (pPreprocessingBase == preprocessingBase) return true;
                else return false;
            });

            // DEBUG: console.log('ONLY LOWEST PREPROCESSING PREVIEWS: ', onlyLatestPreprocessingPreviews);

            // Sort the previews in *reverse* creation order so that the first one is the latest one
            /* const timeComparatorForPreview = (pa, pb) => {
                let a = pa.preview_output[0];
                let b = pb.preview_output[0];
                if ( b.start_time > a.start_time ) {
                    return 1;
                } else if ( b.start_time < a.start_time ) {
                    return -1;
                } else {
                    return 0;
                }
            } */

            // Sort previews in reverse phase retrieval orderm so that least phase is the first one in the list
            const deltaMuComparatorForPreview = (pa, pb) => {
                let a = pa?.preview_input.generic_preview_input.phase_retrieval_settings.phase_retrieval;
                let b = pb?.preview_input.generic_preview_input.phase_retrieval_settings.phase_retrieval;
                if ( b.delta / b.mu > a.delta / a.mu ) {
                    return 1;
                } else if ( a.delta / a.mu > b.delta / b.mu ) {
                    return -1;
                } else {
                    return 0;
                }
            };

            let latestPreprocessingPreviews = [...onlyLatestPreprocessingPreviews].slice();

            // Now that we only have latest preprocessing previews,
            // sort the previews in lowest-to-highest phase retrieval order (delta/mu ratio)
            previews = latestPreprocessingPreviews?.sort(deltaMuComparatorForPreview);
            console.log("PH-SORTED PREVIEWS: ", previews);

            // If we aren't already viewing some preview, default to the lowest phase value
            if ($selectedPreview && $finalFdkSettings?.phase_retrieval_dataset_id?.length > 0) {
                // Do nothing (i.e. keep the current phase retrieval base)
            } else if (!$finalFdkSettings?.phase_retrieval_dataset_id || $finalFdkSettings?.phase_retrieval_dataset_id?.length == 0) {
                // Reset phase retreival base to lowest only if we are not already viewing 
                // somethoing or waiting for a preview to be completed
                // To find the latest phase retrieval base, we should disregard beamhard cupping previews
                let centershiftPreviews = previews?.filter(p => p.preview_input.preview_input.preview_type == "WIDE_CENTERSHIFT");
                phBase = getLowestPhaseRetrievalBase(centershiftPreviews);
                console.log("€€€€€€€€€€€€€€€€€€€ LOWEST PH BASE: ", phBase);
                console.log('€€€€€€€€€€€€€€€€€€€ SETTING PHDATASETIDS BECAUSE NO PHDATASETIDS: ', phaseRetrievalDatasetIds, ' --> ', phBase);
                phaseRetrievalDatasetIds = phBase;
            }

            let phFilteredPreviews = getPreviewsByPhaseRetrievalDatasetId(previews, phaseRetrievalDatasetIds);
            console.log("PH-FILTERED PREVIEWS: ", phFilteredPreviews);

            // If no sliceIndex set, default to the first one
            // TODO: Perhaps sanity-check the current sliceIndex so that we don't try to reload a bad value
            // TODO: Also check if there is a phFilteredPreviews index set
            // so that we can return to same state after refresh (initialiseWithData called again)
            if (!isNumeric(sliceIndex)) {
                sliceIndex = phFilteredPreviews[0]?.preview_output?.[0].slice;
            }
            console.log("SLICE INDEX:", sliceIndex);
            
            // Update all arrays that are used by the specialised preview components
            allPhaseRetrievalPreviewSettings = getAllPhaseRetrievalPreviewSettings(previews);
            centershiftSlicePreviews = getCentershiftPreviewsBySlice(phFilteredPreviews, sliceIndex);
            allBeamhardCuppingSlicePreviews = getBeamhardCuppingPreviewsBySlice(phFilteredPreviews, sliceIndex);
            beamhardCuppingSlicePreviews = filterPreviewSlicesByCentershiftAndTilt(allBeamhardCuppingSlicePreviews);
            phaseRetrievalSlicePreviews = getCentershiftPreviewsBySlice(phFilteredPreviews, sliceIndex);

            console.log(`ONMOUNT CENTERSHIFT PREVIEWS FOR SLICE ${sliceIndex} `, centershiftSlicePreviews);
            console.log(`ONMOUNT BEAMHARD_CUPPING PREVIEWS FOR SLICE ${sliceIndex} `, beamhardCuppingSlicePreviews);
            console.log(`ONMOUNT PHASE_RETRIEVAL PREVIEWS FOR SLICE ${sliceIndex} `, phaseRetrievalSlicePreviews);

            if (!$selectedPreview) {
                // Special BEAMHARD_CUPPING handling: If we don't have a selected preview,
                // stay in the beamhard cupping view anyway.
                if (currentPreviewViewType !== "BEAMHARD_CUPPING") {
                    const preview = centershiftSlicePreviews[0];
                    console.log("FIRST PREVIEW IS: ", preview);
                    console.log("SELECTED PREVIEW TYPE IS: ", preview?.preview_input.preview_input?.preview_type);
                    let selectedPreviewType = preview?.preview_input.preview_input?.preview_type;
                    if (["CENTERSHIFT", "BEAMHARD_CUPPING", "PHASE_RETRIEVAL"].includes(selectedPreviewType)) {
                        currentPreviewViewType = selectedPreviewType;
                    } else if (selectedPreviewType == "WIDE_CENTERSHIFT") {
                        currentPreviewViewType = "CENTERSHIFT";
                    }

                    // Setting selectedPreview will trigger a new render
                    $selectedPreview = preview;
                }
            }

            // NOTE: Changed behaviour: Instead of showing all cs and bh slices in the slicepicker, we'll
            // filter to only the ones pertaining to the current ph filter
            filterAvailableSlices(phFilteredPreviews);

            updateMaximumVolume($selectedPreview);
        }

        // Upon initialisation, mark CENTERSHIFT as selected
        check("CENTERSHIFT");
    }

    let AUTO_REFRESH_ENABLED = true; //DEBUG: Set to false if you want to turn of auto-refresh
    let enableRefresh = false; // By default, disable the refresh button (currently a debug mode feature anyway)

    onMount(() => {
        const failedHandler = (e) => {
            // DEBUG: console.log('RECIPE NOTICED THAT SOMETHING FAILED!', e);
        };
        const doneHandler = (e) => {
            // DEBUG: console.log('RECIPE NOTICED THAT SOMETHING DONE!', e);
            // Check if pipeline may be relevant for the current tomography
            // by comparing pipeline_ids with this page's params session_id
            // (NOTE: pipelines don't report tomography_id, so we can't compare that)
            if (e.detail?.some(p => p?.pipeline_id.includes(params.session))) {
                enableRefresh = true; // Enable Refresh button
                if (AUTO_REFRESH_ENABLED) {
                    refreshPage();
                }
            }
        };
        $pipelineObserver.events.addEventListener("pipelinesfailed", failedHandler);
        $pipelineObserver.events.addEventListener("pipelinesdone", doneHandler);

        // onDestroy:
        return () => {
            $pipelineObserver.events.removeEventListener("pipelinesfailed", failedHandler);
            $pipelineObserver.events.removeEventListener("pipelinesdone", doneHandler);
        };
    });

    async function refreshPage() {
        console.log('----------------------REFRESHING RECIPE PAGE CONTENTS...');
        await initialiseWithData();
        enableRefresh = false; // Disable Refresh button
    }
</script>

<Header />

<main>
    <nav>
        <a href="/" use:link>Home</a>
        &nbsp;
        <a href="/details/{params.session}" use:link>Details</a>
    </nav>
    <div>
        {params.session}/{params.tomography}&nbsp;({$isAuthenticated ? "Authenticated" : "Not authenticated"})
        {#if $showDebug}
            <div style="display: inline-block; vertical-align: middle; margin-left: 16px;">
                <div style="display: flex;">
                    <!-- svelte-ignore a11y-invalid-attribute -->
                    <button class="icon-and-text-button {enableRefresh ? '' : 'disabled'}" type="button" on:click={refreshPage}>
                        <Refresh color="currentColor" size="1.5em"/>Refresh page
                    </button>
                </div>
            </div>
        {/if}
    </div>
    <prepare-pane>
        <slice-selection-pane>
            <div><h3>Select slice for preview...</h3></div>
            <br />
            <SlicePicker
                {tomography}
                currentPreviewType={currentPreviewViewType}
                bind:sliceIndex
                {centershiftSlices}
                {beamhardCuppingSlices}
                snap={true} />
            &nbsp;
            <br />
            &nbsp;
            <br />
            &nbsp;
            <br />
            <button class:active={currentPreviewViewType == "CENTERSHIFT"} on:click={showCentershiftPreview}>
                Centershift ({getNumberOfAvailablePreviewsForSlice(
                    centershiftSlicePreviews,
                    sliceIndex,
                    "CENTERSHIFT"
                )})
            </button>
            <button
                class:active={currentPreviewViewType == "PHASE_RETRIEVAL"}
                class:disabled={!isPhaseRetrievalPreviewAvailable(checked)}
                on:click={showPhaseRetrievalPreview}>
                Phase retrieval ({getNumberOfAvailablePreviewsForSlice(
                    phaseRetrievalSlicePreviews,
                    sliceIndex,
                    "CENTERSHIFT"
                )})
            </button>
            <button
                class:active={currentPreviewViewType == "BEAMHARD_CUPPING"}
                class:disabled={!isBeamhardCuppingPreviewAvailable(checked)}
                on:click={showBeamhardCuppingPreview}>
                Beamhard cupping ({getNumberOfAvailablePreviewsForSlice(
                    beamhardCuppingSlicePreviews,
                    sliceIndex,
                    "BEAMHARD_CUPPING"
                )})
            </button>
            <button
                class:active={currentPreviewViewType == "FINAL"}
                class:disabled={!isFinalPreviewAvailable(checked)}
                on:click={showFinalPreview}>
                Final preview
            </button>
            <h3>Current settings</h3>
            <annotation style="max-width: 256px; margin-left: 0">
                Slice range: {$finalFdkSettings?.min_slice}..{$finalFdkSettings?.max_slice}
                <br />
                Slice roi: {$finalFdkSettings?.slice_roi?.left}..{$finalFdkSettings?.slice_roi?.right}
                <br />
                Angle offset: {$finalFdkSettings?.angle_offset} &deg;
                <br />
                {#if $showDebug}
                    Tilt: {#if isNumeric($finalFdkSettings?.tilt_deg)} {$finalFdkSettings?.tilt_deg} &deg;{/if}
                    <br />
                {/if}
                Centershift: {#if isNumeric($finalFdkSettings?.centershift_object_mm) } <span use:tooltip={{content: `${$finalFdkSettings?.centershift_object_mm} mm`, placement: "top"}}> {($finalFdkSettings?.centershift_object_mm * 1000).toPrecision(3)} μm</span> {/if}
                <br />
                {#if $showDebug}
                    Tolerance: {#if isNumeric($finalFdkSettings?.centershift_tolerance_mm)} {$finalFdkSettings?.centershift_tolerance_mm?.toPrecision(2)} mm {/if}
                    <br />
                    Optimise: {#if $finalFdkSettings?.centershift_tolerance_mm !== undefined} {$finalFdkSettings?.centershift_optimization} {/if}
                    <br />
                {/if}
                Delta: {#if isNumeric($finalFdkSettings?.delta)} <span use:tooltip={{content: `${$finalFdkSettings?.delta}`, placement: "top"}}>{$finalFdkSettings?.delta?.toExponential()}</span> {/if}
                <br />
                Mu: {#if isNumeric($finalFdkSettings?.mu)} {$finalFdkSettings?.mu?.toPrecision(3)} {/if}
                <br />
                {#if $showDebug}
                    Ph dataset: {#if $finalFdkSettings?.phase_retrieval_dataset_id} {$finalFdkSettings?.phase_retrieval_dataset_id} {/if}
                    <br />
                    Ph filter: {phaseRetrievalDatasetIds}
                    <br />
                    Has beamhard cupping slices: {hasBeamhardCuppingSlicePreviews}
                    <br />
                {/if}
                Beamhard cupping: {#if isNumeric($finalFdkSettings?.beamhard_cupping)} {$finalFdkSettings?.beamhard_cupping} {/if}
                <br />
                <!-- Commented out because we have no support for this yet
                Beamhard streaks: {$finalFdkSettings?.beamhard_streaks_factor}
                <br />
                Beamhard streaks threshold: {$finalFdkSettings?.beamhard_streaks_threshold}
                <br />
                -->
                <!-- Commented out because we cannot set this in the GUI
                Localized: {#if $finalFdkSettings?.is_localized !== undefined} {$finalFdkSettings?.is_localized} {/if}
                <br />
                -->
                Output format: {#if $finalFdkSettings?.output_format?.label} {$finalFdkSettings?.output_format?.label} {/if}
                <br />
                {#if $showDebug}
                    Last image index: {$lastPreviewImageIndex}
                    <br />
                    Last BH image index: {#if $lastBeamhardCuppingPreviewImageIndex} {$lastBeamhardCuppingPreviewImageIndex} {/if}
                    <br />
                    Last CS image index: {#if $lastCentershiftPreviewImageIndex} {$lastCentershiftPreviewImageIndex} {/if}
                    <br />
                    Final preview type: {finalPreviewType}
                {/if}
            </annotation>
            <annotation style="max-width: 256px; margin-left: 0">
                <!--pre>{checked?.length > 0 ? formatJson(checked): 'NO PREVIEW CHECKED'}</pre-->
                <p>
                    CENTERSHIFT: {isChecked(checked, "CENTERSHIFT") ? "✓" : "?"}
                    <br />
                    BEAMHARD CUPPING: {isChecked(checked, "BEAMHARD_CUPPING") ? "✓" : "?"}
                    <br />
                    PHASE RETRIEVAL: {isChecked(checked, "PHASE_RETRIEVAL") ? "✓" : "?"}
                    <br />
                </p>
            </annotation>
        </slice-selection-pane>
        <preview-pane>
            {#if currentPreviewViewType === "CENTERSHIFT"}          
                <WideCentershiftPreview
                {params}
                {sliceIndex}
                {centershiftSlices}
                {centershiftSlicePreviews}
                {hasBeamhardCuppingSlicePreviews}
                {allPhaseRetrievalPreviewSettings}
                on:carousel={showCentershiftPreview} />
            {:else if currentPreviewViewType === "BEAMHARD_CUPPING"}
                <BeamhardCuppingPreview
                    {params}
                    {sliceIndex}
                    {centershiftSlices}
                    {beamhardCuppingSlices}
                    {beamhardCuppingSlicePreviews}
                    {allBeamhardCuppingSlicePreviews}
                    on:carousel={showBeamhardCuppingPreview} />
            {:else if currentPreviewViewType === "PHASE_RETRIEVAL"}
                <PhaseRetrievalPreview
                    {params}
                    {sliceIndex}
                    {centershiftSlices}
                    {phaseRetrievalSlicePreviews}
                    bind:phaseRetrievalDatasetIds
                    on:carousel={showPhaseRetrievalPreview} />
            {:else if currentPreviewViewType === "FINAL"}
                <FinalPreview 
                    {params} 
                    {sliceIndex} 
                    {finalBeamhardCuppingOrCentershiftSlicePreviews} />
            {:else}
                <Preview />
            {/if}
        </preview-pane>
    </prepare-pane>
    {#if $showDebug}
        <DataTree
            json={getPreviewsByType(
                previews,
                getActualCurrentPreviewType(currentPreviewViewType))
            }
            {sliceIndex}
            previewType={currentPreviewViewType}
            {phaseRetrievalDatasetIds}
            selectedPreview={$selectedPreview} />
    {/if}
    <annotation>NOTE! All times in UTC!</annotation>
</main>

<style>
    main {
        text-align: left;
        padding: 0;
        margin: 0 auto;
        display: block;
    }

    @media (min-width: 640px) {
        main {
            max-width: none;
        }
    }

    prepare-pane {
        display: flex;
        flex-wrap: wrap;
    }
    slice-selection-pane {
        border: 1px solid var(--var-theme-base-color);
        background-color: var(--var-theme-base-background-color);
        padding: 8px;
        margin: 8px;
        display: flex;
        flex-wrap: wrap;
        flex-direction: column;
    }

    preview-pane {
        border: 1px solid var(--var-theme-base-color);
        background-color: var(--var-theme-base-background-color);
        padding: 8px 24px 24px 24px;
        margin: 8px;
        display: block;
    }
</style>
